import { useSnackbar } from 'notistack';
import { useApi } from '../../../shared/context';
import { useMutation, useQueryClient } from 'react-query';
import { Dialog, DialogContent, DialogTitle, Grid } from '@mui/material';
import {
	CerebroDropdown,
	CerebroDropdownMenuItem,
	CerebroForm,
	CerebroLabel,
	CerebroMaskedInput,
	CerebroPrimaryButton,
	CerebroSecondaryButton,
	CerebroText,
} from '../../../shared/components';
import { Field, Formik, FormikErrors } from 'formik';
import { FormikUtils, NumberUtils } from '../../../shared/utils';
import { CerebroApiEndpoints } from '../../../shared/utils/api';
import { QueryKeys } from '../../../shared/hooks';
import { EmptyGuid } from '../../../shared/constants';
import {
	TermSheetFacility,
	TermSheetFacilityFee,
	TermSheetFee,
	TermSheetFeeFrequencies,
	TermSheetFeeFrequenciesMap,
	TermSheetFeeTypes,
	TermSheetFeeTypesMap,
} from '../../../shared/models';
import { MaxCurrencyLength } from '../../../shared/utils/platform';
import { getApiResponseData } from '../../../shared/utils/axios';

interface FeeFormDialogProps {
	dataRoomGuid: string | undefined;
	termSheetGuid: string | undefined;
	revisionGuid: string | undefined;
	facility: TermSheetFacility | null;
	fee: TermSheetFacilityFee | null;
	termSheetFees: TermSheetFee[] | undefined;
	close: () => void;
}

export const FeeFormDialog: React.FC<FeeFormDialogProps> = ({
	dataRoomGuid,
	termSheetGuid,
	revisionGuid,
	facility,
	fee,
	termSheetFees,
	close,
}) => {
	const api = useApi();
	const snackbar = useSnackbar();
	const client = useQueryClient();

	const feeMutation = useMutation(
		async ({ values }: FeeFormMutationVariables) => {
			const patchValues = {
				termSheetFeeGuid: values.termSheetFeeGuid,
				feeAmount: NumberUtils.ParseDecimal(values.feeAmount, undefined),
				termSheetFeeType: values.termSheetFeeType,
				feeFrequency: values.feeFrequency,
			};

			const { data, error } = await getApiResponseData<TermSheetFacilityFee>(
				fee?.guid == EmptyGuid
					? api.post(
							CerebroApiEndpoints.TermSheets.FacilityFees(
								dataRoomGuid,
								termSheetGuid,
								revisionGuid,
								facility?.guid
							),
							patchValues
					  )
					: api.patch(
							CerebroApiEndpoints.TermSheets.SpecificFacilityFee(
								dataRoomGuid,
								termSheetGuid,
								revisionGuid,
								facility?.guid,
								fee?.guid
							),
							patchValues
					  )
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: () => {
				client.invalidateQueries(
					QueryKeys.TermSheets.FacilityFees(dataRoomGuid, termSheetGuid, revisionGuid, facility?.guid)
				);
				client.invalidateQueries(QueryKeys.TermSheets.Specific(dataRoomGuid, termSheetGuid));
				client.invalidateQueries(QueryKeys.TermSheets.Revision(dataRoomGuid, termSheetGuid, revisionGuid));
				snackbar.enqueueSnackbar('Facility fee was successfully saved.', {
					variant: 'success',
				});
				close();
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
				close();
			},
		}
	);

	var validateForm = (values: FeeFormValues): FeeFormErrors => {
		const errors: FeeFormErrors = {};

		if (!values.termSheetFeeGuid) {
			errors.termSheetFeeGuid = 'Selecting a fee is required.';
		}

		if (!values.feeAmount) {
			errors.feeAmount = 'Fee amount is required.';
		}

		if (!values.termSheetFeeType) {
			errors.termSheetFeeType = 'Fee type is required.';
		}

		if (!values.feeFrequency) {
			errors.feeFrequency = 'Fee frequency is required.';
		}

		return errors;
	};

	return (
		<Dialog open={fee !== null} onClose={close}>
			<DialogTitle>Fees</DialogTitle>
			<DialogContent>
				<Formik
					initialValues={{
						termSheetFeeGuid: fee?.termSheetFee.guid,
						feeAmount:
							(fee?.termSheetFeeType ?? 'Currency') === 'Currency'
								? NumberUtils.FormatDecimal(fee?.feeAmount)
								: NumberUtils.FormatFloat(fee?.feeAmount, 8),
						termSheetFeeType: fee?.termSheetFeeType ?? 'Currency',
						feeFrequency: fee?.feeFrequency ?? 'Annually',
					}}
					validate={validateForm}
					onSubmit={FormikUtils.MutateOnSubmit(feeMutation.mutate)}
				>
					{({ values, isSubmitting, errors, touched, handleSubmit, setFieldValue }) => (
						<CerebroForm onSubmit={handleSubmit}>
							<Grid item xs={11}>
								<CerebroLabel required id="termSheetFeeGuid">
									Fee
								</CerebroLabel>
								<Field
									id="termSheetFeeGuid"
									as={CerebroDropdown}
									name="termSheetFeeGuid"
									disabled={isSubmitting}
									error={touched.termSheetFeeGuid && errors.termSheetFeeGuid !== undefined}
									onChange={(e: any) => {
										const targetGuid = e.target.value;
										setFieldValue('termSheetFeeGuid', targetGuid);
										const sheetFee = termSheetFees?.find(w => w.guid == targetGuid);
										if (sheetFee) {
											setFieldValue('termSheetFeeType', sheetFee.termSheetFeeType);
											setFieldValue('feeFrequency', sheetFee.feeFrequency);
										}
									}}
								>
									{termSheetFees
										?.sort((a, b) => a.feeName.localeCompare(b.feeName))
										.map(item => (
											<CerebroDropdownMenuItem key={item.guid} value={item.guid}>
												{item.feeName}
											</CerebroDropdownMenuItem>
										))}
								</Field>
								{errors.termSheetFeeGuid && (
									<CerebroText variant="error" sx={{ marginBottom: 4 }}>
										{errors.termSheetFeeGuid}
									</CerebroText>
								)}
							</Grid>
							<Grid item xs={11}>
								<CerebroLabel required id="termSheetFeeType">
									Type
								</CerebroLabel>
								<Field
									id="termSheetFeeType"
									as={CerebroDropdown}
									name="termSheetFeeType"
									disabled={isSubmitting}
									error={touched.termSheetFeeType && errors.termSheetFeeType !== undefined}
								>
									{Object.entries(TermSheetFeeTypesMap).map(([value, description]) => (
										<CerebroDropdownMenuItem key={value} value={value}>
											{description}
										</CerebroDropdownMenuItem>
									))}
								</Field>
								{errors.termSheetFeeType && (
									<CerebroText variant="error" sx={{ marginBottom: 4 }}>
										{errors.termSheetFeeType}
									</CerebroText>
								)}
							</Grid>
							<Grid item xs={11}>
								<CerebroLabel id="feeAmount" required>
									Amount
								</CerebroLabel>
								{isCurrencyFee(values) ? (
									<Field
										formatas="currency"
										as={CerebroMaskedInput}
										maxLength={MaxCurrencyLength}
										id="feeAmount"
										name="feeAmount"
										disabled={isSubmitting}
										error={touched.feeAmount && errors.feeAmount !== undefined}
									/>
								) : (
									<Field
										as={CerebroMaskedInput}
										allowDecimals={true}
										minDecimalPlaces={1}
										maxDecimalPlaces={8}
										id="feeAmount"
										name="feeAmount"
										disabled={isSubmitting}
										error={touched.feeAmount && errors.feeAmount !== undefined}
									/>
								)}
								{errors.feeAmount && (
									<CerebroText variant="error" sx={{ marginBottom: 4 }}>
										{errors.feeAmount}
									</CerebroText>
								)}
							</Grid>
							<Grid item xs={11}>
								<CerebroLabel required id="feeFrequency">
									Frequency
								</CerebroLabel>
								<Field
									id="feeFrequency"
									as={CerebroDropdown}
									name="feeFrequency"
									disabled={isSubmitting}
									error={touched.feeFrequency && errors.feeFrequency !== undefined}
								>
									{Object.entries(TermSheetFeeFrequenciesMap).map(([value, description]) => (
										<CerebroDropdownMenuItem key={value} value={value}>
											{description}
										</CerebroDropdownMenuItem>
									))}
								</Field>
								{errors.feeFrequency && (
									<CerebroText variant="error" sx={{ marginBottom: 4 }}>
										{errors.feeFrequency}
									</CerebroText>
								)}
							</Grid>

							<CerebroPrimaryButton type="submit" loading={isSubmitting} sx={{ marginTop: 6 }}>
								Save
							</CerebroPrimaryButton>
							<CerebroSecondaryButton sx={{ marginTop: 6, marginLeft: 1 }} onClick={() => close()}>
								Cancel
							</CerebroSecondaryButton>
						</CerebroForm>
					)}
				</Formik>
			</DialogContent>
		</Dialog>
	);
};

type FeeFormErrors = FormikErrors<FeeFormValues>;
type FeeFormMutationVariables = FormikUtils.MutateOnSubmitVariables<FeeFormValues>;
interface FeeFormValues {
	termSheetFeeGuid: string | undefined;
	feeAmount: string;
	termSheetFeeType: TermSheetFeeTypes;
	feeFrequency: TermSheetFeeFrequencies;
}

function isCurrencyFee(value: FeeFormValues): boolean {
	return value.termSheetFeeType == 'Currency';
}
